import { db } from '../../core/firebase/admin.js'
import type { SearchQuery, SearchResponse, SearchResult, SearchFacets, DiscoveryFeed } from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import type { FirestoreDoc, FirestoreDocLike, FirestoreQuery } from '../../common/types/firestore.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


const logger = createLogger('SearchRepository')

export class SearchRepository {
  private readonly shareLinksCollection = db.collection('shareLinks')

  async search(query: SearchQuery): Promise<SearchResponse> {
    logger.debug('Building search query', { q: query.q, page: query.page, filters: Object.keys(query.filters || {}).length })
    const page = query.page || 1
    const limit = query.limit || 20
    const offset = (page - 1) * limit

    let firestoreQuery = this.shareLinksCollection
      .where('privacy', '==', 'public') as FirestoreQuery

    // Apply text filter (Firestore doesn't support full-text search, use basic contains)
    // NOTE: In production, replace with Algolia/Meilisearch
    if (query.q) {
      logger.debug('Text filter applied', { q: query.q })
      // Basic implementation: filter in-memory after fetch
      // Production: Use external search service
    } else if (Object.keys(query.filters || {}).length === 0) {
      // No query and no filters: return empty or trending
      // For now, let's return empty to avoid fetching everything
      return {
        results: [],
        total: 0,
        page: 1,
        limit,
        facets: { countries: [], tags: [], durationRanges: [], distanceRanges: [] }
      }
    }

    // Apply country filter
    if (query.filters?.countries?.length) {
      logger.debug('Country filter applied', { countries: query.filters.countries.length })
      firestoreQuery = firestoreQuery.where(
        'metadata.countries',
        'array-contains-any',
        query.filters.countries
      ) as FirestoreQuery
    }

    // Apply tag filter
    if (query.filters?.tags?.length) {
      logger.debug('Tag filter applied', { tags: query.filters.tags.length })
      firestoreQuery = firestoreQuery.where(
        'metadata.tags',
        'array-contains-any',
        query.filters.tags
      ) as FirestoreQuery
    }

    // Fetch results with field projection (optimize query)
    logger.debug('Executing search query')
    const snapshot = await firestoreQuery
      .select(
        'planName',
        'planTitle',
        'planDescription',
        'planId',
        'thumbnail',
        'metadata',
        'stagesCount',
        'authorId',
        'authorName',
        'authorAvatar',
        'viewCount',
        'views',
        'shares',
        'comments',
        'createdAt',
        'privacy',
        'isActive',
        'expiresAt',
        'createdBy'
      )
      .get()

    const now = Date.now()
    let results: SearchResult[] = []
    for (const doc of snapshot.docs as FirestoreDoc[]) {
      if (!this.isShareLinkDocumentEligible(doc, now)) {
        continue
      }
      results.push(this.mapDocToResult(doc))
    }

    // Apply text filter in-memory (fallback)
    if (query.q) {
      const searchTerm = query.q.toLowerCase()
      results = results.filter((r: SearchResult) =>
        r.name.toLowerCase().includes(searchTerm) ||
        r.description.toLowerCase().includes(searchTerm) ||
        r.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm))
      )
    }

    // Apply distance filters
    if (query.filters?.minDistance) {
      results = results.filter((r: SearchResult) => r.totalDistance >= query.filters!.minDistance!)
    }
    if (query.filters?.maxDistance) {
      results = results.filter((r: SearchResult) => r.totalDistance <= query.filters!.maxDistance!)
    }

    // Apply duration filters
    if (query.filters?.minDuration) {
      results = results.filter((r: SearchResult) => r.totalDuration >= query.filters!.minDuration!)
    }
    if (query.filters?.maxDuration) {
      results = results.filter((r: SearchResult) => r.totalDuration <= query.filters!.maxDuration!)
    }

    // Pagination
    const total = results.length
    const paginatedResults = results.slice(offset, offset + limit)

    logger.info('Search completed', { total, page, limit, resultsInPage: paginatedResults.length, applied: query.q ? ['text'] : [] })

    // Calculate facets
    const facets = this.calculateFacets(results)

    return {
      results: paginatedResults,
      total,
      page,
      limit,
      facets
    }
  }

  async getDiscoveryFeed(): Promise<DiscoveryFeed> {
    logger.debug('Building discovery feed')
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

    try {
      // Trending: Most likes in last 7 days
      logger.debug('Fetching trending plans from last 7 days')
      const trendingSnapshot = await this.shareLinksCollection
        .where('privacy', '==', 'public')
        .where('createdAt', '>=', sevenDaysAgo)
        .orderBy('createdAt', 'desc')
        .orderBy('likes', 'desc')
        .limit(10)
        .get()

      // Recent: Newest plans
      logger.debug('Fetching recent plans')
      const recentSnapshot = await this.shareLinksCollection
        .where('privacy', '==', 'public')
        .orderBy('createdAt', 'desc')
        .limit(10)
        .get()

      // Popular: Most likes all-time
      logger.debug('Fetching popular plans')
      const popularSnapshot = await this.shareLinksCollection
        .where('privacy', '==', 'public')
        .orderBy('likes', 'desc')
        .limit(10)
        .get()

      const now = Date.now()
      const trending = trendingSnapshot.docs
        .filter((doc: FirestoreDoc) => this.isShareLinkDocumentEligible(doc, now))
        .map((doc) => this.mapDocToResult(doc))
      const recent = recentSnapshot.docs
        .filter((doc: FirestoreDoc) => this.isShareLinkDocumentEligible(doc, now))
        .map((doc) => this.mapDocToResult(doc))
      const popular = popularSnapshot.docs
        .filter((doc: FirestoreDoc) => this.isShareLinkDocumentEligible(doc, now))
        .map((doc) => this.mapDocToResult(doc))

      logger.info('Discovery feed queries completed', {
        trending: trending.length,
        recent: recent.length,
        popular: popular.length
      })

      return {
        trending,
        recent,
        popular
      }
    } catch (error) {
      if (this.isMissingIndexError(error)) {
        logger.warn('Missing Firestore index for discovery feed queries, using fallback ranking', {
          message: (error as Error).message
        })
        return this.buildDiscoveryFeedFallback(sevenDaysAgo)
      }

      logger.error('Discovery feed query failed', { error })
      throw error
    }
  }

  async indexPlan(planId: string): Promise<void> {
    logger.debug('Indexing plan for search', { planId })
    // NOTE: In production, send to Algolia/Meilisearch
    // For now, this is a no-op (Firestore is the "index")
    // Future implementation: sync plan data to search service
    logger.info('Plan indexed', { planId })
  }

  async removePlan(planId: string): Promise<void> {
    logger.debug('Removing plan from search index', { planId })
    // NOTE: In production, remove from Algolia/Meilisearch
    // For now, this is a no-op (Firestore shares are marked inactive)
    // Future implementation: delete from search service index
    logger.info('Plan removed from index', { planId })
  }

  private calculateFacets(results: SearchResult[]): SearchFacets {
    logger.debug('Calculating search facets', { resultsCount: results.length })
    const countries = new Map<string, number>()
    const tags = new Map<string, number>()

    for (const result of results) {
      for (const country of result.countries) {
        countries.set(country, (countries.get(country) || 0) + 1)
      }
      for (const tag of result.tags) {
        tags.set(tag, (tags.get(tag) || 0) + 1)
      }
    }

    logger.info('Facets calculated', { countriesCount: countries.size, tagsCount: tags.size })

    return {
      countries: Array.from(countries.entries()).map(([value, count]) => ({ value, count })),
      tags: Array.from(tags.entries()).map(([value, count]) => ({ value, count })),
      durationRanges: [],
      distanceRanges: []
    }
  }

  private normalizeDate(value: unknown): Date {
    if (value instanceof Date) {
      return value
    }

    if (
      value &&
      typeof value === 'object' &&
      'toDate' in value &&
      typeof (value as { toDate: () => Date }).toDate === 'function'
    ) {
      return (value as { toDate: () => Date }).toDate()
    }

    if (typeof value === 'number') {
      return new Date(value)
    }

    if (isString(value)) {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return parsed
      }
    }

    return new Date(0)
  }

  private async buildDiscoveryFeedFallback(sevenDaysAgo: Date): Promise<DiscoveryFeed> {
    logger.debug('Building discovery feed fallback')
    const snapshot = await this.shareLinksCollection.limit(200).get()
    const now = Date.now()
    const docs = snapshot.docs.filter((doc: FirestoreDoc) => this.isShareLinkDocumentEligible(doc, now))

    if (docs.length === 0) {
      logger.warn('Discovery feed fallback has no eligible documents')
      return { trending: [], recent: [], popular: [] }
    }

    const trendingDocs = this.sortDocsByLikes(docs)
      .filter((doc) => this.normalizeDate(doc.data()?.createdAt).getTime() >= sevenDaysAgo.getTime())
      .slice(0, 10)
    const recentDocs = this.sortDocsByDate(docs).slice(0, 10)
    const popularDocs = this.sortDocsByLikes(docs).slice(0, 10)

    logger.info('Discovery feed fallback generated', {
      trending: trendingDocs.length,
      recent: recentDocs.length,
      popular: popularDocs.length
    })

    return {
      trending: trendingDocs.map((doc) => this.mapDocToResult(doc)),
      recent: recentDocs.map((doc) => this.mapDocToResult(doc)),
      popular: popularDocs.map((doc) => this.mapDocToResult(doc))
    }
  }

  private mapDocToResult(doc: FirestoreDocLike): SearchResult {
    const data = (doc.data() ?? {}) as Record<string, unknown>
    const metadata =
      typeof data.metadata === 'object' && data.metadata !== null
        ? (data.metadata as Record<string, unknown>)
        : {}

    const planName =
      typeof data.planName === 'string'
        ? data.planName
        : typeof data.planTitle === 'string'
          ? data.planTitle
          : 'Untitled Plan'

    const countries = Array.isArray(metadata.countries)
      ? metadata.countries.filter((value) => typeof value === 'string') as string[]
      : []
    const tags = Array.isArray(metadata.tags)
      ? metadata.tags.filter((value) => typeof value === 'string') as string[]
      : []

    const views = typeof data.viewCount === 'number'
      ? data.viewCount
      : typeof data.views === 'number'
        ? data.views
        : 0

    const authorId =
      typeof data.authorId === 'string'
        ? data.authorId
        : typeof data.createdBy === 'string'
          ? data.createdBy
          : 'unknown'

    return {
      id: doc.id,
      planId: typeof data.planId === 'string' ? data.planId : undefined,
      name: planName,
      description: typeof data.planDescription === 'string' ? data.planDescription : '',
      coverImage: typeof data.thumbnail === 'string' ? data.thumbnail : undefined,
      totalDistance: typeof metadata.distance === 'number' ? metadata.distance : 0,
      totalDuration: typeof metadata.duration === 'number' ? metadata.duration : 0,
      stagesCount: typeof data.stagesCount === 'number' ? data.stagesCount : 0,
      countries,
      tags,
      author: {
        id: authorId,
        name: typeof data.authorName === 'string' ? data.authorName : 'Anonymous',
        avatar: typeof data.authorAvatar === 'string' ? data.authorAvatar : undefined
      },
      stats: {
        views,
        shares: typeof data.shares === 'number' ? data.shares : 0,
        comments: typeof data.comments === 'number' ? data.comments : 0
      },
      createdAt: this.normalizeDate(data.createdAt),
      relevance: 1
    }
  }

  private isShareLinkDocumentEligible(doc: FirestoreDoc, now: number): boolean {
    const data = doc.data()
    if (!data) {
      return false
    }

    const privacy =
      typeof data.privacy === 'string'
        ? data.privacy
        : typeof data.visibility === 'string'
          ? data.visibility
          : undefined

    if (privacy && privacy !== 'public') {
      return false
    }

    const isActive = typeof data.isActive === 'boolean' ? data.isActive : true
    if (!isActive) {
      return false
    }

    const expiresAtValue = data.expiresAt
    if (expiresAtValue) {
      const expiresAt = this.normalizeDate(expiresAtValue)
      if (expiresAt.getTime() > 0 && expiresAt.getTime() < now) {
        return false
      }
    }

    return true
  }

  private isMissingIndexError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false
    }

    const code = (error as { code?: string | number }).code
    const message = (error as { message?: string }).message ?? ''
    return code === 'failed-precondition' || code === 9 || message.includes('index')
  }

  private sortDocsByLikes(docs: FirestoreDocLike[]): FirestoreDocLike[] {
    return [...docs].sort((a, b) => this.getDocLikes(b) - this.getDocLikes(a))
  }

  private sortDocsByDate(docs: FirestoreDocLike[]): FirestoreDocLike[] {
    return [...docs].sort((a, b) => this.getDocTimestamp(b) - this.getDocTimestamp(a))
  }

  private getDocLikes(doc: FirestoreDocLike): number {
    const likes = doc.data()?.likes
    return typeof likes === 'number' ? likes : 0
  }

  private getDocTimestamp(doc: FirestoreDocLike): number {
    const createdAt = doc.data()?.createdAt
    return this.normalizeDate(createdAt).getTime()
  }
}
